/*
 * SYRVAIN — dithering shader hero
 * Vanilla WebGL. No framework, no dependencies.
 * Renders a source image (the SYRVAIN camel) as an animated ordered-dither field.
 *
 * The canvas backing store is rendered at 1/pxSize of the display size and
 * upscaled with image-rendering:pixelated, so one fragment == one dither cell.
 * That keeps the fragment count low and the dither grid perfectly crisp.
 */
(function () {
  'use strict';

  var VERT = [
    'attribute vec2 aPos;',
    'void main() { gl_Position = vec4(aPos, 0.0, 1.0); }'
  ].join('\n');

  var FRAG = [
    'precision highp float;',
    '',
    'uniform vec2      uRes;',
    'uniform float     uTime;',
    'uniform vec3      uBack;',
    'uniform vec3      uFront;',
    'uniform float     uType;',
    'uniform float     uHasTex;',
    'uniform sampler2D uTex;',
    'uniform vec2      uImg;',
    'uniform float     uScale;',
    'uniform float     uInvert;',
    'uniform float     uWarp;',
    'uniform float     uCut;',
    'uniform float     uOffY;',
    '',
    'float hash(vec2 p) {',
    '  p = fract(p * vec2(123.34, 456.21));',
    '  p += dot(p, p + 45.32);',
    '  return fract(p.x * p.y);',
    '}',
    '',
    'float vnoise(vec2 p) {',
    '  vec2 i = floor(p);',
    '  vec2 f = fract(p);',
    '  vec2 u = f * f * (3.0 - 2.0 * f);',
    '  float a = hash(i);',
    '  float b = hash(i + vec2(1.0, 0.0));',
    '  float c = hash(i + vec2(0.0, 1.0));',
    '  float d = hash(i + vec2(1.0, 1.0));',
    '  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);',
    '}',
    '',
    'float fbm(vec2 p) {',
    '  float v = 0.0;',
    '  float amp = 0.5;',
    '  for (int i = 0; i < 4; i++) {',
    '    v += amp * vnoise(p);',
    '    p *= 2.02;',
    '    amp *= 0.5;',
    '  }',
    '  return v;',
    '}',
    '',
    'float Bayer2(vec2 a) { a = floor(a); return fract(a.x / 2.0 + a.y * a.y * 0.75); }',
    '#define Bayer4(a) (Bayer2(0.5 * (a)) * 0.25 + Bayer2(a))',
    '#define Bayer8(a) (Bayer4(0.5 * (a)) * 0.25 + Bayer2(a))',
    '',
    'void main() {',
    '  vec2 cell = floor(gl_FragCoord.xy);',
    '  vec2 puv  = gl_FragCoord.xy / uRes;',
    '  float aspect = uRes.x / uRes.y;',
    '',
    '  float v = 0.0;',
    '',
    '  if (uHasTex > 0.5) {',
    '    float imgAspect = uImg.x / uImg.y;',
    '    vec2 iuv = puv - 0.5;',
    '    if (aspect > imgAspect) { iuv.x *= aspect / imgAspect; }',
    '    else                    { iuv.y *= imgAspect / aspect; }',
    '    iuv /= uScale;',
    '',
    '    float w = fbm(puv * 2.4 + vec2(uTime * 0.12, uTime * 0.09));',
    '    iuv += (w - 0.5) * uWarp;',
    '',
    '    iuv.y = -iuv.y;',
    '    iuv += 0.5;',
    '    iuv.y += uOffY;',
    '',
    '    if (iuv.x >= 0.0 && iuv.x <= 1.0 && iuv.y >= 0.0 && iuv.y <= 1.0) {',
    '      vec4 t = texture2D(uTex, iuv);',
    '      float lum = dot(t.rgb, vec3(0.299, 0.587, 0.114));',
    '      v = lum * t.a;',
    '      // Black point: the logo sits on a near-black plate (lum ~0.055).',
    '      // Cutting it away keeps the background free of stray dither cells.',
    '      v = clamp((v - uCut) / max(0.0001, 1.0 - uCut), 0.0, 1.0);',
    '    }',
    '  } else {',
    '    vec2 p = (puv - 0.5) * vec2(aspect, 1.0) / uScale;',
    '    float r = length(p);',
    '    float R = 0.36;',
    '    float inside = 1.0 - step(R, r);',
    '    float z = sqrt(max(0.0, R * R - r * r));',
    '    vec3 n = normalize(vec3(p, z));',
    '    vec3 l = normalize(vec3(cos(uTime * 0.5) * 0.8, 0.45, sin(uTime * 0.5) * 0.8 + 0.6));',
    '    v = inside * max(0.0, dot(n, l));',
    '  }',
    '',
    '  if (uInvert > 0.5) { v = 1.0 - v; }',
    '',
    '  float shim = fbm(puv * 3.5 + vec2(uTime * 0.25, -uTime * 0.18));',
    '  v = clamp(v * (0.70 + 0.58 * shim), 0.0, 1.0);',
    '',
    '  float th;',
    '  if      (uType < 0.5) { th = hash(cell + floor(uTime * 22.0)); }',
    '  else if (uType < 1.5) { th = Bayer2(cell); }',
    '  else if (uType < 2.5) { th = Bayer4(cell); }',
    '  else                  { th = Bayer8(cell); }',
    '',
    '  // Strict compare, not step(): step(th, v) returns 1.0 when th == v == 0.0,',
    '  // which lights every cell whose Bayer value is zero even on pure background.',
    '  float bit = (v > th) ? 1.0 : 0.0;',
    '  gl_FragColor = vec4(mix(uBack, uFront, bit), 1.0);',
    '}'
  ].join('\n');

  function hexToRgb(hex) {
    var h = String(hex || '').replace('#', '').trim();
    if (h.length === 3) { h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]; }
    if (h.length !== 6) { return [0, 0, 0]; }
    var n = parseInt(h, 16);
    return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
  }

  function compile(gl, type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.warn('[syrvain-dither] shader: ' + gl.getShaderInfoLog(s));
      gl.deleteShader(s);
      return null;
    }
    return s;
  }

  function Dither(root) {
    var canvas = root.querySelector('[data-dither-canvas]');
    if (!canvas) { return; }

    var opts = {
      src:     root.getAttribute('data-src') || '',
      imgW:    parseFloat(root.getAttribute('data-img-w')) || 1,
      imgH:    parseFloat(root.getAttribute('data-img-h')) || 1,
      back:    hexToRgb(root.getAttribute('data-back') || '#000000'),
      front:   hexToRgb(root.getAttribute('data-front') || '#E0982E'),
      px:      Math.max(1, parseFloat(root.getAttribute('data-px')) || 2),
      speed:   parseFloat(root.getAttribute('data-speed')),
      type:    parseFloat(root.getAttribute('data-type')) || 0,
      scale:   parseFloat(root.getAttribute('data-scale')) || 0.72,
      invert:  root.getAttribute('data-invert') === 'true' ? 1 : 0,
      warp:    parseFloat(root.getAttribute('data-warp')),
      cut:     parseFloat(root.getAttribute('data-cut')),
      offy:    parseFloat(root.getAttribute('data-offy')),
      scaleMb: parseFloat(root.getAttribute('data-scale-mobile'))
    };
    // A narrow viewport fits the logo by width, which leaves it small in a tall
    // hero — so mobile gets its own scale.
    if (isNaN(opts.scaleMb)) { opts.scaleMb = opts.scale; }
    if (isNaN(opts.speed)) { opts.speed = 1.0; }
    if (isNaN(opts.warp))  { opts.warp = 0.012; }
    if (isNaN(opts.cut))   { opts.cut = 0.12; }
    if (isNaN(opts.offy))  { opts.offy = 0.0; }

    // Header/nav instances follow the light/dark toggle instead of a fixed
    // plate color — see assets/syrvain-theme-switcher.js applyPreference().
    if (root.hasAttribute('data-dither-follows-theme')) {
      var themeNow = document.documentElement.getAttribute('data-syrvain-theme');
      opts.back = hexToRgb(themeNow === 'light' ? '#FFFFFF' : '#0A0A0A');
    }

    var gl = canvas.getContext('webgl', { antialias: false, alpha: false, depth: false })
          || canvas.getContext('experimental-webgl', { antialias: false, alpha: false, depth: false });

    if (!gl) {
      // No WebGL: leave the static fallback image visible and bail out.
      root.setAttribute('data-dither-state', 'unsupported');
      return;
    }

    var vs = compile(gl, gl.VERTEX_SHADER, VERT);
    var fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) { root.setAttribute('data-dither-state', 'unsupported'); return; }

    var prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) {
      console.warn('[syrvain-dither] link: ' + gl.getProgramInfoLog(prog));
      root.setAttribute('data-dither-state', 'unsupported');
      return;
    }
    gl.useProgram(prog);

    var buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    var aPos = gl.getAttribLocation(prog, 'aPos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    var U = {};
    ['uRes', 'uTime', 'uBack', 'uFront', 'uType', 'uHasTex', 'uTex', 'uImg', 'uScale', 'uInvert', 'uWarp', 'uCut', 'uOffY']
      .forEach(function (n) { U[n] = gl.getUniformLocation(prog, n); });

    gl.uniform3fv(U.uBack, opts.back);
    gl.uniform3fv(U.uFront, opts.front);
    gl.uniform1f(U.uType, opts.type);
    gl.uniform1f(U.uScale, opts.scale);
    gl.uniform1f(U.uInvert, opts.invert);
    gl.uniform1f(U.uWarp, opts.warp);
    gl.uniform1f(U.uCut, opts.cut);
    gl.uniform1f(U.uOffY, opts.offy);
    gl.uniform1f(U.uHasTex, 0);
    gl.uniform2f(U.uImg, opts.imgW, opts.imgH);
    gl.uniform1i(U.uTex, 0);

    // Lets other scripts (the light/dark switcher) recolor the plate live
    // without tearing down and rebuilding the whole WebGL context.
    root.__syrvainDitherSetBack = function (hex) {
      gl.uniform3fv(U.uBack, hexToRgb(hex));
      draw(last);
    };

    // ---- texture -----------------------------------------------------------
    var tex = gl.createTexture();
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE,
                  new Uint8Array([0, 0, 0, 255]));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

    if (opts.src) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.uniform2f(U.uImg, img.naturalWidth || opts.imgW, img.naturalHeight || opts.imgH);
        gl.uniform1f(U.uHasTex, 1);
        root.setAttribute('data-dither-state', 'ready');
        draw(last);
      };
      img.onerror = function () {
        // Texture failed (CORS/404) — fall back to the procedural sphere.
        console.warn('[syrvain-dither] image failed, using sphere fallback');
        root.setAttribute('data-dither-state', 'ready');
      };
      img.src = opts.src;
    } else {
      root.setAttribute('data-dither-state', 'ready');
    }

    // ---- sizing ------------------------------------------------------------
    var vw = 0, vh = 0, curScale = null;

    function resize() {
      var r = canvas.getBoundingClientRect();
      var w = Math.max(1, Math.round(r.width / opts.px));
      var h = Math.max(1, Math.round(r.height / opts.px));

      var s = window.innerWidth < 750 ? opts.scaleMb : opts.scale;
      if (s !== curScale) { curScale = s; gl.uniform1f(U.uScale, s); }

      if (w === vw && h === vh) { return false; }
      vw = w; vh = h;
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
      gl.uniform2f(U.uRes, w, h);
      return true;
    }

    // ---- loop --------------------------------------------------------------
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    var raf = 0, start = performance.now(), last = 0, visible = true;

    function draw(t) {
      gl.uniform1f(U.uTime, t);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    function frame(now) {
      raf = requestAnimationFrame(frame);
      resize();
      last = ((now - start) / 1000) * opts.speed;
      draw(last);
    }

    function play() {
      if (raf || !visible) { return; }
      if (reduce && reduce.matches) { resize(); draw(last); return; }
      start = performance.now() - (last / (opts.speed || 1)) * 1000;
      raf = requestAnimationFrame(frame);
    }

    function pause() {
      if (raf) { cancelAnimationFrame(raf); raf = 0; }
    }

    resize();
    draw(0);

    // Only animate while the hero is actually on screen.
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        if (visible) { play(); } else { pause(); }
      }, { threshold: 0.01 }).observe(root);
    } else {
      play();
    }

    document.addEventListener('visibilitychange', function () {
      if (document.hidden) { pause(); } else { play(); }
    });

    if (reduce && reduce.addEventListener) {
      reduce.addEventListener('change', function () { pause(); play(); });
    }

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        if (resize() && !raf) { draw(last); }
      }).observe(canvas);
    } else {
      window.addEventListener('resize', function () {
        if (resize() && !raf) { draw(last); }
      });
    }

    play();
    root.__syrvainDitherStop = pause;
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-syrvain-dither]').forEach(function (el) {
      if (el.__syrvainDitherInit) { return; }
      el.__syrvainDitherInit = true;
      try { Dither(el); } catch (e) { console.warn('[syrvain-dither]', e); }
    });
  }

  // Public hook so other scripts (the light/dark switcher) can recolor every
  // theme-following instance live, without touching the fixed-plate hero.
  window.SyrvainDither = window.SyrvainDither || {};
  window.SyrvainDither.setBack = function (hex, scope) {
    (scope || document).querySelectorAll('[data-syrvain-dither][data-dither-follows-theme]').forEach(function (el) {
      if (el.__syrvainDitherSetBack) { el.__syrvainDitherSetBack(hex); }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Shopify theme editor: re-init when the section is re-rendered.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    var el = e.target.querySelector('[data-syrvain-dither]');
    if (el && el.__syrvainDitherStop) { el.__syrvainDitherStop(); }
  });
})();
